import { Form, Radio } from "antd";
import { Point } from "tm-shared/types";
import {
    createMaxIntegerSanitizer,
    normalizeDecimalInput,
} from "tm-shared/utils";

import { ContractFormValues } from "@/types/form";
import { handleFocus } from "@/utils/dom/handleFocus";

import { StyledInputNumber } from "../../Styled";
import { AssortmentMatrix } from "../AssortmentMatrix";
import { Discount } from "../Discount";
import { Facings } from "../Facings";
import { PaidProductInput } from "../PaidProductInput";
import { RetroBonusPercentage } from "../RetroBonusPercentage";

interface ContractTypeFieldsProps {
    point: Point | null;
    pointLoading: boolean;
}

export const ContractTypeFields: React.FC<ContractTypeFieldsProps> = ({
    point,
    pointLoading,
}) => {
    const form = Form.useFormInstance();
    const contractTypeField = Form.useWatch("type", form);

    const parseIntegerMax30 = createMaxIntegerSanitizer(30);

    switch (contractTypeField) {
        case "paidProductInput":
            return (
                <PaidProductInput point={point} pointLoading={pointLoading} />
            );
        case "assortmentMatrix":
            return (
                <AssortmentMatrix point={point} pointLoading={pointLoading} />
            );
        case "facings":
            return <Facings />;
        case "discount":
            return <Discount point={point} pointLoading={pointLoading} />;
        case "shelfPayment":
            return (
                <>
                    <Form.Item>
                        <Radio.Group
                            defaultValue={1}
                            options={[
                                { value: 1, label: "% від обороту" },
                                { value: 2, label: "Ставка в грн." },
                            ]}
                        />
                    </Form.Item>
                    <Form.Item<ContractFormValues>
                        name="paymentTerms"
                        rules={[{ required: true, message: "Введіть число" }]}
                    >
                        <StyledInputNumber
                            min={0}
                            onFocus={handleFocus}
                            textAlign="left"
                            inputMode="numeric"
                            placeholder="Введіть число"
                            parser={normalizeDecimalInput}
                        />
                    </Form.Item>
                </>
            );
        case "additionalSalesPlace":
            return (
                <Form.Item<ContractFormValues>
                    name="placePrice"
                    label="Вкажіть вартість місця"
                    rules={[
                        {
                            required: true,
                            message: "Вкажіть вартість місця",
                        },
                    ]}
                >
                    <StyledInputNumber
                        min={0}
                        onFocus={handleFocus}
                        textAlign="left"
                        inputMode="numeric"
                        placeholder="Поле для введення"
                        style={{ width: "100%" }}
                        parser={normalizeDecimalInput}
                    />
                </Form.Item>
            );
        case "marketingCatalog":
            return (
                <Form.Item<ContractFormValues>
                    name="materialsPrice"
                    label="Вкажіть вартість матеріалів"
                    rules={[
                        {
                            required: true,
                            message: "Вкажіть вартість матеріалів",
                        },
                    ]}
                >
                    <StyledInputNumber
                        min={0}
                        onFocus={handleFocus}
                        textAlign="left"
                        inputMode="numeric"
                        placeholder="Поле для введення"
                        style={{ width: "100%" }}
                        parser={normalizeDecimalInput}
                    />
                </Form.Item>
            );
        case "deferredCredit":
            return (
                <>
                    <Form.Item<ContractFormValues>
                        name="deferredDelivery"
                        label="Відстрочка доставки"
                        rules={[
                            { required: true, message: "Вкажіть відстрочку" },
                        ]}
                    >
                        <StyledInputNumber
                            min={0}
                            max={30}
                            onFocus={handleFocus}
                            textAlign="left"
                            inputMode="numeric"
                            placeholder="Поле для введення"
                            style={{ width: "100%" }}
                            parser={parseIntegerMax30}
                        />
                    </Form.Item>
                    <Form.Item<ContractFormValues>
                        name="deferredTermForm1"
                        label="Відстрочка по Формі 1"
                        rules={[
                            { required: true, message: "Вкажіть відстрочку" },
                        ]}
                    >
                        <StyledInputNumber
                            min={0}
                            max={30}
                            onFocus={handleFocus}
                            textAlign="left"
                            inputMode="numeric"
                            placeholder="Поле для введення"
                            style={{ width: "100%" }}
                            parser={parseIntegerMax30}
                        />
                    </Form.Item>
                    <Form.Item<ContractFormValues>
                        name="deferredTermForm2"
                        label="Відстрочка по Формі 2"
                        rules={[
                            { required: true, message: "Вкажіть відстрочку" },
                        ]}
                    >
                        <StyledInputNumber
                            min={0}
                            max={30}
                            onFocus={handleFocus}
                            textAlign="left"
                            inputMode="numeric"
                            placeholder="Поле для введення"
                            style={{ width: "100%" }}
                            parser={parseIntegerMax30}
                        />
                    </Form.Item>
                </>
            );
        case "retroBonusFixed":
            return (
                <Form.Item<ContractFormValues>
                    name="costInCash"
                    label="Вкажіть суму витрат:"
                    rules={[{ required: true, message: "Вкажіть суму витрат" }]}
                >
                    <StyledInputNumber
                        min={0}
                        onFocus={handleFocus}
                        textAlign="left"
                        inputMode="numeric"
                        placeholder="00"
                        style={{ width: "100%" }}
                        parser={normalizeDecimalInput}
                    />
                </Form.Item>
            );
        case "retroBonusPercentage":
            return <RetroBonusPercentage />;
        case "agreement":
        case "license":
            return null;
        default:
            return null;
    }
};
